import {
  TRACKED_EVENT_NAME,
  TrackedEventName,
  TrackedEventProps,
} from '@/constants';
import { validateGtag } from '@/utils';
import { getLandingAttributionData } from '@/utils/landing-attribution';
import { useLocale } from 'next-intl';
import { useCallback } from 'react';

export const useAddClickButtonGtag = ({
  label,
  position,
}: TrackedEventProps) => {
  const locale = useLocale();

  const addGtag = useCallback(
    (customLabel?: string, params?: Record<string, unknown>) => {
      if (!validateGtag()) return;

      window.gtag('event', TRACKED_EVENT_NAME.CLICK_BUTTON, {
        event_label: customLabel ? `${label}_${customLabel}` : label,
        position,
        user_language: locale,
        ...params,
      });
    },
    [label, position, locale],
  );

  return addGtag;
};

export const useToggleGtag = ({ label, position }: TrackedEventProps) => {
  const addGtag = useAddClickButtonGtag({ label: `${label}_open`, position });
  const removeGtag = useAddClickButtonGtag({
    label: `${label}_toggle-close`,
    position,
  });

  const handleToggle = useCallback(
    (e: React.ToggleEvent<HTMLDetailsElement>) => {
      if (e.currentTarget.open) {
        addGtag();
      } else {
        removeGtag();
      }
    },
    [addGtag, removeGtag],
  );
  return handleToggle;
};

export type FormInteractionType = 'click' | 'change' | 'focus' | 'blur';

export const useFormInteractionGtag = ({ label }: { label: string }) => {
  const locale = useLocale();
  const sentFields = new Set<string>();
  const addGtag = useCallback(
    (type: FormInteractionType, field_name: string) => {
      if (!validateGtag() || sentFields.has(field_name)) return;

      window.gtag('event', TRACKED_EVENT_NAME.FORM_INTERACTION, {
        event_label: label,
        type,
        field_name,
        user_language: locale,
      });

      sentFields.add(field_name);
    },
    [label, sentFields],
  );

  return addGtag;
};

export type GenerateLeadType = 'success' | 'fail';

const useLeadGtagBase = (
  eventName: TrackedEventName,
  { label, position }: { label: string; position?: string },
) => {
  const locale = useLocale();
  const addGtag = useCallback(
    (type: GenerateLeadType) => {
      if (!validateGtag()) return;

      const {
        landingPath,
        landingSource,
        landingMedium,
        landingCampaign,
        landingReferrer,
        landingFrom,
      } = getLandingAttributionData();

      window.gtag('event', eventName, {
        event_label: label,
        ...(position && { position }),
        user_language: locale,
        type,
        ...(landingPath && { landing_path: landingPath }),
        ...(landingSource && { landing_source: landingSource }),
        ...(landingMedium && { landing_medium: landingMedium }),
        ...(landingCampaign && { landing_campaign: landingCampaign }),
        ...(landingReferrer && { landing_referrer: landingReferrer }),
        ...(landingFrom && { landing_from: landingFrom }),
      });
    },
    [eventName, label, position, locale],
  );

  return addGtag;
};

export const useGenerateLeadGtag = ({ label }: { label: string }) =>
  useLeadGtagBase(TRACKED_EVENT_NAME.GENERATE_LEAD, { label });

export const useGenerateMiddleLeadGtag = ({
  label,
  position,
}: TrackedEventProps) =>
  useLeadGtagBase(TRACKED_EVENT_NAME.GENERATE_MIDDLE_LEAD, { label, position });

export const useFormSubmitGtag = ({ label, position }: TrackedEventProps) => {
  const locale = useLocale();

  const sendFormSubmitEvent = useCallback(
    <T extends Record<string, unknown> = Record<string, never>>(
      options?: T,
    ) => {
      if (!validateGtag()) return;

      window.gtag('event', TRACKED_EVENT_NAME.FORM_SUBMIT, {
        event_label: label,
        position,
        user_language: locale,
        ...options,
      });
    },
    [label, position, locale],
  );

  return sendFormSubmitEvent;
};
